import React, { FC, useMemo, useEffect, ReactNode } from 'react'
import theme from '../styles/theme'
import {
  Cloud,
  renderSimpleIcon,
  fetchSimpleIcons,
  ICloud,
} from 'react-icon-cloud'
import * as app from '../styles/App.styled'
import * as techStack from '../styles/TechStack.styled'
import SlideInMotion, {
  SlideInDirection,
} from './SlideInMotion'

// NOTE My penultimate attempt yielded inconsistencies between dev and prod build: 
// the icon colors where set to black in prod. Commenting out motion-related settings 
// like initial, clickToFront, maxSpeed, and minSpeed effectively removed the dynamic 
// spinning behavior. This change stabilized the rendering of the icons in the cloud 
// and prevented potential production inconsistencies. By avoiding initial motion 
// (initial), spin speed (maxSpeed, minSpeed), and interactive adjustments like 
// clickToFront, the cloud remains static, reducing complexity and improving 
// reliability. This suggests the rendering issues were likely tied to how motion
// was calculated or handled, either by the library or within this environment.

const IconCloud = () => {
  const iconSlugs = useMemo(() => [
    'antdesign',
    'apollographql',
    'amazonwebservices',
    // 'assemblyscript',
    'axios',
    'babel',
    'centos',
    'chai',
    'curl',
    'css3',
    'cypress',
    'debian',
    'deno',
    'dotenv',
    'docker',
    'express',
    'eslint',
    'expo',
    'formik',
    'github',
    'gnu',
    'gnubash',
    'graphql',
    'html5',
    'javascript',
    'jest',
    'json',
    'jsonwebtokens',
    // 'libreoffice',
    'linux',
    'lucide',
    'macos',
    'markdown',
    'mocha',
    'mongodb',
    'mongoose',
    'mui',
    'nginx',
    'npm',
    'nodedotjs',
    'nodemon',
    'oracle',
    'passport',
    'postgresql',
    'python',
    'pm2',
    // 'postman',
    // 'raspberrypi',
    'react',
    'reacthookform',
    'reactrouter',
    'redis',
    'redhat',
    'redux',
    'remix',
    'render',
    'rust',
    'sentry',
    'sequelize',
    'socketdotio',
    'stripe',
    'styled-components',
    'stylelint',
    'swagger',
    'tailwindcss',
    'testinglibrary',
    'tsnode',
    'typescript',
    'ubuntu',
    'webpack',
    'webassembly',
    // 'v8',
    'vercel',
    'vim',
    // 'visualstudiocode',
    'zod',
  ], [])

  const cloudProps = useMemo<
    Omit<ICloud, 'children'>
  >(() => {
    return {
      containerProps: {
        style: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '320px',
          padding: 0,
        },
      },
      options: {
        reverse: false,
        depth: 1,
        wheelZoom: false,
        imageScale: 2,
        activeCursor: false,
        outlineColour: '#0000',
        dragControl: false,
        // DO NOT USE
        // initial: [0.015, -0.015],
        // clickToFront: 500,
        // tooltipDelay: 0,
        // maxSpeed: 0.01,
        // minSpeed: 0.01,

      },
    }
  }, [])

  type IconData = { simpleIcons:{ hex:string }[] }
  const [icons, setIcons] = React.useState<IconData|null>(null)

  useEffect(() => {
    const getAndSetIcons = async () => {
      const results = await fetchSimpleIcons({
        slugs: iconSlugs,
      })

      console.log(results)  // NOTE see "allIcon"

      setIcons(results)
    }

    getAndSetIcons()
  }, [
    iconSlugs,
    setIcons,
  ])

  const renderedIcons = useMemo<ReactNode>(() => {
    return (!icons) ?
      <>loading...</>
      :
      <>
        {
          // see props for bgHex, fallbackHex, minContrastRatio:
          // https://github.com/teaguestockwell/react-icon-cloud?tab=readme-ov-file

          Object.values(icons.simpleIcons)
            .map((icon) => {
              return renderSimpleIcon({
                icon,  // NOTE you could use this to add custom icons
                size: 42,
                aProps: {
                  style: {},
                  onClick: (e) => e.preventDefault(),
                },
                bgHex: theme.colors.techSlugs.bgHex,
                fallbackHex: theme.colors.techSlugs.fallbackHex,
                minContrastRatio: 2,  // default 1 (0-21)
              })

              // const {
              //   title,
              //   children: {
              //     props: {
              //       alt,
              //       height,
              //       width,
              //       src,
              //     },
              //   },
              // } = renderSimpleIcon({
              //   icon,
              //   size: 42,
              //   aProps: {
              //     onClick: (e) => e.preventDefault(),
              //   },
              // }).props

              // const replaceDetectedRgb = ({
              //   srcStr,
              //   newHex,
              // }:{
              //   srcStr:string,
              //   newHex:string,
              // }) => {
              //   const hexToRgb = (hex:string) => {
              //     hex = hex.startsWith('#') ? hex.slice(1) : hex

              //     const r = parseInt(hex.slice(0, 2), 16)
              //     const g = parseInt(hex.slice(2, 4), 16)
              //     const b = parseInt(hex.slice(4, 6), 16)

              //     return `rgb(${r}, ${g}, ${b})`
              //   }

              //   const newRgb = hexToRgb(newHex)

              //   const rgbRegex = /rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)/g

              //   return srcStr.replace(rgbRegex, newRgb)
              // }

              // const newSrc = replaceDetectedRgb({
              //   srcStr: src,
              //   newHex: icon.hex,
              // })

              // return (
              //   <a
              //     href='https://emojipedia.org/globe-showing-americas/'
              //     title={title}
              //     key={title}
              //   >
              //     <img
              //       height={height}
              //       width={width}
              //       alt={alt}
              //       src={newSrc}
              //     />
              //   </a>
              // )
            })
        }
      </>
  }, [icons])

  return (
    <Cloud { ...cloudProps }>
      {renderedIcons}
    </Cloud>
  )
}

export const TechStack:FC = () => {
  const { left, right } = SlideInDirection

  return (
    <app.StyledSection $willReverse={true}>
      <app.StyledCardLargeLightBlack>
        <techStack.StyledPillContainer>
          <SlideInMotion direction={left}>
            <techStack.StyledPill>JavaScript, TypeScript, Python, SQL, Bash</techStack.StyledPill>
          </SlideInMotion>
          <SlideInMotion direction={right}>
            <techStack.StyledPill>MongoDB, Postgres, Oracle</techStack.StyledPill>
          </SlideInMotion>
          <SlideInMotion direction={left}>
            <techStack.StyledPill>Node, Express, PM2, Mongoose, GraphQL</techStack.StyledPill>
          </SlideInMotion>
          <SlideInMotion direction={right}>
            <techStack.StyledPill>React, Redux, Webpack, Styled Components</techStack.StyledPill>
          </SlideInMotion>
        </techStack.StyledPillContainer>
      </app.StyledCardLargeLightBlack>
      <app.StyledCardSmallDarkBlack>
        <IconCloud />
      </app.StyledCardSmallDarkBlack>
    </app.StyledSection>
  )
}
